<template>
  <b-modal
    ref="upsertModal"
    v-model="showModal"
    centered
    size="md"
    :no-close-on-backdrop="true"
    cancel-variant="danger"
    title="Add Product"
    @hidden="clearForm"
  >
    <b-overlay
      variant="transparent"
      opacity="0.6"
      :show="isFetching"
    >
      <ValidationObserver ref="vObserver">
        <b-col cols="12">
          <h5> Select product:</h5>
          <ValidationProvider
            v-slot="{ errors }"
            name="v-product-selector"
            rules="required"
          >
            <b-form-group>
              <v-select
                v-model="selectedItems"
                multiple
                :options="optionItems"
                class="select-size-lg"
                label="name"
                placeholder="Select product"
              >
                <template #option="optionItem">
                  <div class="d-flex">
                    <b-img
                      rounded
                      height="48"
                      width="48"
                      :src="optionItem.product.image"
                    />
                    <div class="ml-2">
                      <p class="p-0 m-0">
                        {{ optionItem.product.name }}
                      </p>
                      <p class="p-0 m-0">
                        count: {{ optionItem.count }}
                      </p>
                      <p class="p-0 m-0">
                        size: {{ optionItem.size }}
                      </p>
                    </div>
                  </div>
                </template>
              </v-select>
              <span class="validation__red">{{ errors[0] }}</span>
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col
          v-for="(selectedItem,bColIdx) in selectedItems"
          :key="'b-col-' + selectedItem.product.name + bColIdx"
          cols="12"
          class="mt-1"
        >
          <label> {{ selectedItem.name }}  </label>
          <ValidationProvider
            v-slot="{ errors }"
            :name="`Product (${bColIdx+1})`"
            :rules="`required|between:1,${selectedItem.count}`"
          >
            <b-form-group>
              <b-form-input
                v-model.number="selectedItems[bColIdx].form.count"
                type="number"
                placeholder="Count"
                :state="errors.length > 0 ? false : null"
                size="lg"
              />
              <span
                v-if="errors[0]"
                class="validation__red"
              >{{ errors[0] }}</span>
            </b-form-group>
          </ValidationProvider>
        </b-col>

      </ValidationObserver>
    </b-overlay>

    <template #modal-footer>
      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
        class="d-inline-block"
      >
        <b-button
          variant="primary"
          @click="submitProducts"
        >
          Add
        </b-button>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  computed, ref, watchEffect,
} from '@vue/composition-api'
import {
  BModal,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BOverlay,
  BFormCheckbox,
  BFormFile,
  BFormTextarea,
  BImg,
  BCard,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import router from '@/router'
import useOrder from '@/composables/useOrder'
import useLoading from '@/composables/useLoading'
import useReception from '@/composables/useReception'

export default {
  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BFormGroup,
    BFormInput,
    BOverlay,
    BFormCheckbox,
    BFormFile,
    BFormTextarea,
    BImg,
    BCard,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    id: {
      type: [Number, String, Object],
      default: null,
    },
    methodType: {
      type: String,
      default: 'create',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:show', 'update:data'],
  setup(props, { emit }) {
    const {
      create,
    } = useReception()

    const { findOne: orderFindOne } = useOrder()

    const {
      isLoading: isFetching,
      startLoading: startFetching,
      finishLoading: finishFetching,
    } = useLoading()

    const {
      isLoading,
      startLoading,
      finishLoading,
    } = useLoading()

    const vObserver = ref(null)
    const orderDetails = ref({
      products: [],
      remainders: [],
    })
    const selectedItems = ref([])

    const showModal = computed({
      get() {
        return props.show
      },
      set(v) {
        emit('update:show', v)
      },
    })

    const optionItems = computed(() => orderDetails.value.remainders.map(item => ({
      ...item,
      name: `${item.product.name} ( ${item.count} )`,
      form: {
        count: null,
      },
    })))

    watchEffect(async () => {
      if (showModal.value) {
        try {
          startFetching()
          const response = await orderFindOne({ id: router.currentRoute.params.id })
          orderDetails.value = response.data.document
        } finally {
          finishFetching()
        }
      }
    })

    function clearForm() {
      selectedItems.value = []
      orderDetails.value = {
        products: [],
        remainders: [],
      }
    }

    async function submitProducts() {
      const isValid = await vObserver.value.validate()

      if (!isValid) {
        return
      }

      try {
        startLoading()
        await create({
          body: {
            // eslint-disable-next-line radix
            order: parseInt(router.currentRoute.params.id),
            products: selectedItems.value.map(product => ({
              size: product.size,
              amount: product.form.count,
              product: product.product.id,
              warehouse: product.warehouse.id,
            })),
          },
        })

        showModal.value = false
        emit('update:data')
        clearForm()
        vObserver.value.reset()
      } finally {
        finishLoading()
      }
    }

    return {
      vObserver,
      isLoading,
      showModal,
      isFetching,
      selectedItems,
      optionItems,

      clearForm,
      submitProducts,
    }
  },
}
</script>
